import { Select as SelectPrimitive } from "@base-ui/react/select"
import classNames from "classnames"
import PropTypes from "prop-types"
import React, { useMemo, useState } from "react"
import {
  FlagImage,
  defaultCountries,
  parseCountry,
  usePhoneInput,
} from "react-international-phone"

import { SelectContent, SelectSeparator } from "src/components/Select"

const PREFERRED_COUNTRIES = ["us"]

const CountryItem = ({ iso2, name, dialCode }) => (
  <SelectPrimitive.Item
    value={iso2}
    className={classNames(
      "flex cursor-pointer items-center gap-2 px-2 py-1.5 text-sm",
      "text-gray-800 outline-none",
      "data-[highlighted]:rounded data-[highlighted]:bg-blue-800 data-[highlighted]:text-white"
    )}
  >
    <FlagImage iso2={iso2} size="20px" />
    <SelectPrimitive.ItemText className="truncate">
      {name}
    </SelectPrimitive.ItemText>
    <span className="ml-auto text-gray-400 group-data-[highlighted]:text-blue-200">
      +{dialCode}
    </span>
  </SelectPrimitive.Item>
)

CountryItem.propTypes = {
  iso2: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  dialCode: PropTypes.string.isRequired,
}

const FormTelephoneField = ({
  disabled,
  hasErrors,
  id,
  name,
  onBlur,
  onChange,
  onFocus,
  placeholder,
  required,
  value,
}) => {
  const [isFocused, setIsFocused] = useState(false)

  const { inputValue, country, setCountry, handlePhoneValueChange, inputRef } =
    usePhoneInput({
      defaultCountry: "us",
      value: value || "",
      preferredCountries: PREFERRED_COUNTRIES,
      disableDialCodeAndPrefix: true,
      onChange: (data) => {
        if (data.inputValue.length) {
          onChange(data.phone)
        } else {
          onChange("")
        }
      },
    })

  const { preferred, rest } = useMemo(() => {
    const parsed = defaultCountries.map(parseCountry)
    return {
      preferred: parsed.filter((c) => PREFERRED_COUNTRIES.includes(c.iso2)),
      rest: parsed.filter((c) => !PREFERRED_COUNTRIES.includes(c.iso2)),
    }
  }, [])

  return (
    <div className="flex w-full">
      <SelectPrimitive.Root
        value={country.iso2}
        onValueChange={(iso2) => setCountry(iso2, { focusOnInput: true })}
        disabled={disabled}
      >
        <SelectPrimitive.Trigger
          className={classNames(
            "flex h-10 shrink-0 items-center gap-1 rounded-l border border-r-0 px-2 outline-none",
            {
              "border-blue-600": isFocused && !hasErrors,
              "border-red-600": hasErrors,
              "cursor-not-allowed bg-gray-100": disabled,
              "bg-white": !disabled,
            }
          )}
        >
          <FlagImage iso2={country.iso2} size="24px" />
          <SelectPrimitive.Icon className="flex items-center">
            <i className="fa-solid fa-chevron-down text-xs text-gray-600" />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
        <SelectContent className="w-[300px]">
          {preferred.map((c) => (
            <CountryItem
              key={c.iso2}
              iso2={c.iso2}
              name={c.name}
              dialCode={c.dialCode}
            />
          ))}
          <SelectSeparator />
          {rest.map((c) => (
            <CountryItem
              key={c.iso2}
              iso2={c.iso2}
              name={c.name}
              dialCode={c.dialCode}
            />
          ))}
        </SelectContent>
      </SelectPrimitive.Root>
      <input
        ref={inputRef}
        type="tel"
        id={id}
        name={name}
        value={inputValue}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        autoComplete="tel"
        onChange={handlePhoneValueChange}
        onFocus={(event) => {
          onFocus && onFocus(event)
          setIsFocused(true)
        }}
        onBlur={(event) => {
          onBlur && onBlur(event)
          setIsFocused(false)
        }}
        className={classNames(
          "h-10 min-w-0 shrink-0 grow rounded-r border px-2 py-3 text-sm outline-none focus:border-blue-600",
          {
            "border-red-600": hasErrors,
            "cursor-not-allowed bg-gray-100 text-gray-500": disabled,
          }
        )}
      />
    </div>
  )
}

FormTelephoneField.displayName = "FormTelephoneField"

FormTelephoneField.propTypes = {
  disabled: PropTypes.bool,
  hasErrors: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.string,
}

export default FormTelephoneField
